import { defineStore } from 'pinia'
import api from '../api'
import DOMPurify from 'dompurify'

// Analisi del formStore per la gestione dello stato e cache
export const useFormStore = defineStore('form', {
  state: () => ({
    forms: [],
    questions: [],
    answers: {},
    customAnswers: {},
    errors: {},
    isLoadingQuestions: false,
    isSubmitted: false,
    isFormSubmitted: false,
    selectedForm: null,
    formStartTime: null,
    conferenceOptions: [],
    isAuthenticated: false,
    user: null,
    router: null,
    // Cache semplificata solo per forms e questions
    formsCache: { data: null, timestamp: null },
    questionsCache: {}, // { formId: { data: [], timestamp: number } }
    // Timer per il controllo aggiornamenti
    updateCheckerTimer: null,
    // Form context - salvato quando si naviga verso un form
    formContext: {
      formId: null,
      eventId: null,
      cycleId: null,
      attemptCode: null,
      titleId: null,
      titleName: null,
      eventDate: null,
      adminMode: false,
      targetPersonId: null,
    },
  }),

  getters: {
    hasAnyAnswer: (state) => {
      return Object.values(state.answers).some((answer) => {
        if (typeof answer === 'number') return answer > 0
        if (Array.isArray(answer)) return answer.length > 0
        return Boolean(answer?.trim?.())
      })
    },

    formProgress: (state) => {
      if (!state.questions.length) return 0

      // Exclude auto-filled title question (Elenco_BFC) from progress calculation
      const requiredQuestions = state.questions.filter((q) => {
        // Exclude auto-filled title question
        const isAutoFilledTitle = q.question_type === 'select' && q.question_text?.toLowerCase().includes('titolo') && q.question_option?.includes('Elenco_BFC')
        return q.is_mandatory && !isAutoFilledTitle
      })

      if (!requiredQuestions.length) return 0 // No required questions = 0% (form is optional)

      const completedAnswers = requiredQuestions.filter((question) => {
        const answer = state.answers[question.id]

        // Handle different answer types
        if (question.question_type === 'rating') {
          // For rating, null means not answered yet, any number (including 0) is valid
          return answer !== null && answer !== undefined && typeof answer === 'number'
        }
        if (question.question_type === 'checkbox') {
          return Array.isArray(answer) && answer.length > 0
        }
        // Handle string answers (text, radio, select)
        return answer && typeof answer === 'string' && answer.trim().length > 0
      })

      return Math.round((completedAnswers.length / requiredQuestions.length) * 100)
    },

    getFormIntro: (state) => (formId) => {
      if (!formId || !state.forms.length) return ''
      const form = state.forms.find((f) => f.id === formId)
      return form ? form.text_intro : ''
    },

    getFormSubmittedText: (state) => (formId) => {
      if (!formId || !state.forms.length) return ''
      const form = state.forms.find((f) => f.id === formId)
      return form ? form.text_form_submitted : ''
    },

    isAdmin: (state) => state.user?.permissions?.includes('*') ?? false,

    isViewer: (state) => state.user?.type_id === 3,
  },

  actions: {
    hasPermission(code) {
      return this.user?.permissions?.includes('*') || this.user?.permissions?.includes(code) || false
    },
    getAllowedConferenceTypes(action) {
      if (this.user?.permissions?.includes('*')) return [1, 2, 3, 4]
      return [1, 2, 3, 4].filter(t => this.user?.permissions?.includes(`admin.conference.${action}.${t}`))
    },
    hasAnyConferencePermission(action) {
      return this.getAllowedConferenceTypes(action).length > 0
    },
    hasAnyCreateConferencePermission() {
      if (this.user?.permissions?.includes('*')) return true
      return [1, 2, 3, 4].some((t) => this.user?.permissions?.includes(`admin.create.conference.${t}`))
    },
    /**
     * Restituisce gli slot interni (be_fit_title) consentiti per l'azione richiesta.
     * Slot tassonomici: conferenza, workshop, funded, corso.external_teacher, corso.
     * @param {'read'|'write'|'create'} action
     * @returns {string[]}
     */
    getAllowedInternalSlots(action) {
      const slots = ['conferenza', 'workshop', 'funded', 'corso.external_teacher', 'corso']
      if (this.user?.permissions?.includes('*')) return slots
      return slots.filter((s) => this.user?.permissions?.includes(`admin.training.internal.${s}.${action}`))
    },
    hasAnyTrainingInternalPermission(action) {
      return this.getAllowedInternalSlots(action).length > 0
    },
    /**
     * Restituisce i format consentiti per l'azione (derivati dagli slot interni)
     * @param {'read'|'write'|'create'} action
     * @returns {string[]} subset di ['conferenza','workshop','corso']
     */
    getAllowedFormats(action) {
      const slots = this.getAllowedInternalSlots(action)
      const formats = new Set()
      for (const s of slots) {
        if (s === 'conferenza') formats.add('conferenza')
        else if (s === 'workshop') formats.add('workshop')
        else if (s === 'funded' || s === 'corso.external_teacher' || s === 'corso') formats.add('corso')
      }
      return [...formats]
    },
    /**
     * True se l'utente puo' impostare is_funded per l'azione richiesta
     */
    hasFundedPermission(action) {
      return this.user?.permissions?.includes('*') ||
        this.user?.permissions?.includes(`admin.training.internal.funded.${action}`)
    },
    /**
     * True se l'utente puo' impostare is_external (corso con docente esterno)
     */
    hasInternalExternalTeacherPermission(action) {
      return this.user?.permissions?.includes('*') ||
        this.user?.permissions?.includes(`admin.training.internal.corso.external_teacher.${action}`)
    },
    hasProposalPermission() {
      return this.hasPermission('admin.training.external.proposal')
    },
    hasExternalFormDefaultsManagePermission() {
      return this.hasPermission('admin.training.external.form_defaults.manage')
    },
    getAllowedForm3Types() {
      if (this.user?.permissions?.includes('*')) return [1, 2, 3, 4]
      return [1, 2, 3, 4].filter(t => this.user?.permissions?.includes(`admin.analytics.form3.read.${t}`))
    },
    hasAnyForm3Permission() {
      return this.getAllowedForm3Types().length > 0 || this.hasPermission('admin.analytics.form3.speaker.read')
    },
    isSpeakerOnlyForm3Mode() {
      return this.hasPermission('admin.analytics.form3.speaker.read') && this.getAllowedForm3Types().length === 0
    },
    // Metodi gestione form context
    setFormContext(context) {
      console.log('[STORE] Salvataggio form context:', context)
      this.formContext = {
        formId: context.formId,
        eventId: context.eventId || null,
        cycleId: context.cycleId || null,
        attemptCode: context.attemptCode || null,
        titleId: context.titleId || null,
        titleName: context.titleName || null,
        eventDate: context.eventDate || null,
        adminMode: context.adminMode || false,
        targetPersonId: context.targetPersonId || null,
      }

      // Salva in sessionStorage per persistere al refresh
      try {
        sessionStorage.setItem('formContext', JSON.stringify(this.formContext))
        console.log('[STORE] Form context salvato in sessionStorage')
      } catch (err) {
        console.error('[STORE] Errore salvataggio sessionStorage:', err)
      }
    },

    restoreFormContext() {
      try {
        const saved = sessionStorage.getItem('formContext')
        if (saved) {
          this.formContext = JSON.parse(saved)
          console.log('[STORE] Form context ripristinato da sessionStorage:', this.formContext)
          return true
        }
      } catch (err) {
        console.error('[STORE] Errore ripristino sessionStorage:', err)
      }
      return false
    },

    clearFormContext() {
      console.log('[STORE] Pulizia form context')
      this.formContext = {
        formId: null,
        eventId: null,
        cycleId: null,
        attemptCode: null,
        titleId: null,
        titleName: null,
        eventDate: null,
        adminMode: false,
        targetPersonId: null,
      }

      // Rimuovi da sessionStorage
      try {
        sessionStorage.removeItem('formContext')
        console.log('[STORE] Form context rimosso da sessionStorage')
      } catch (err) {
        console.error('[STORE] Errore rimozione sessionStorage:', err)
      }
    },

    // Metodi cache semplificati
    invalidateFormsCache() {
      this.formsCache = { data: null, timestamp: null }
      console.log('[CACHE] Cache forms invalidata')
    },

    invalidateQuestionsCache(formId = null) {
      if (formId) {
        delete this.questionsCache[formId]
        console.log(`[CACHE] Cache questions invalidata per form ${formId}`)
      } else {
        this.questionsCache = {}
        console.log('[CACHE] Cache questions completamente invalidata')
      }

      // Rimuovi anche da sessionStorage
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('questions_')) {
          if (!formId || key === `questions_${formId}`) {
            sessionStorage.removeItem(key)
          }
        }
      })
    },

    // Invalida tutta la cache (solo forms e questions)
    invalidateAllDataCache() {
      this.invalidateFormsCache()
      this.invalidateQuestionsCache()
      console.log('[CACHE] Tutta la cache dati invalidata')
    },

    async loadForms(force = false) {
      // Verifica cache se non forziamo il reload
      if (!force && this.formsCache.data && this.formsCache.timestamp) {
        const cacheAge = Date.now() - this.formsCache.timestamp
        // Cache valida per 5 minuti
        if (cacheAge < 5 * 60 * 1000) {
          console.log('[CACHE] Utilizzo forms dalla cache')
          this.forms = this.formsCache.data
          return this.formsCache.data
        }
      }

      try {
        console.log('[API] Caricamento forms da server')
        const response = await api.get('/forms', {
          params: {
            showHidden: this.showHiddenForms,
            _t: Date.now(), // Cache busting
          },
        })

        const formsData = response.data.forms || response.data
        this.forms = formsData
        this.formsCache = { data: formsData, timestamp: Date.now() }
        console.log('[API] Form caricati:', formsData.length)
        return this.forms
      } catch (error) {
        console.error('Errore nel caricamento dei form:', error)
        throw error
      }
    },
    handleFormSelection(formId) {
      console.log('[DEBUG FORM] Form selected:', formId)
      this.selectedForm = formId

      // Clear previous questions when changing form
      this.questions = []
      this.clearAnswers()

      // Carica le domande immediatamente
      this.loadQuestions().catch((err) => {
        console.error('[DEBUG FORM] Errore nel caricamento delle domande:', err)
      })
    },

    // Flag per tenere traccia delle richieste in corso
    _loadingQuestionsPromises: {},

    async loadQuestions(force = false) {
      console.log('[DEBUG QUESTIONS] loadQuestions chiamato con:', {
        form: this.selectedForm,
        force,
        formsCount: this.forms.length,
      })

      try {
        this.formStartTime = new Date().toISOString()

        if (!this.selectedForm) {
          console.log('[DEBUG QUESTIONS] Form non selezionato')
          return
        }

        // Se abbiamo già domande per questo form in memoria e non forziamo
        if (!force && this.questions.length > 0 && this.questions[0]?.form_id === this.selectedForm) {
          console.log('[DEBUG QUESTIONS] Domande già caricate in memoria')
          return
        }

        // Verifica cache
        const cached = this.questionsCache[this.selectedForm]
        if (!force && cached?.data) {
          const cacheAge = Date.now() - cached.timestamp
          // Cache valida per 10 minuti
          if (cacheAge < 10 * 60 * 1000) {
            console.log(`[CACHE] Utilizzo ${cached.data.length} domande dalla cache`)
            this.questions = cached.data
            this.initializeAnswers()
            return cached.data
          }
        }

        // Fallback a sessionStorage
        const questionsKey = `questions_${this.selectedForm}`
        if (!force) {
          const cachedQuestions = sessionStorage.getItem(questionsKey)
          if (cachedQuestions) {
            try {
              const parsedQuestions = JSON.parse(cachedQuestions)
              console.log(`[SESSION] Utilizzo ${parsedQuestions.length} domande dalla sessionStorage`)
              this.questions = parsedQuestions
              // Aggiorna cache in memoria
              this.questionsCache[this.selectedForm] = {
                data: parsedQuestions,
                timestamp: Date.now(),
              }
              this.initializeAnswers()
              return parsedQuestions
            } catch (e) {
              console.warn('Errore parsing cache domande:', e)
              sessionStorage.removeItem(questionsKey)
            }
          }
        }

        this.isLoadingQuestions = true

        try {
          // Trova i dettagli del form
          const selectedForm = this.forms.find((form) => form.id === this.selectedForm)

          if (!selectedForm) {
            console.log('[DEBUG QUESTIONS] Form non trovato')
            return
          }

          // Parametri per la chiamata API
          const params = { _t: Date.now() } // Cache busting
          if (selectedForm.requires_role_filter && this.user) {
            const roleValue = this.user.training_path || this.user.percorso_formativo
            if (roleValue) {
              params.role = roleValue
              console.log(`[DEBUG QUESTIONS] Aggiunto filtro ruolo: ${roleValue}`)
            }
          }

          console.log(`[API] Chiamata API a /forms/${this.selectedForm}/questions`, params)
          const response = await api.get(`/forms/${this.selectedForm}/questions`, {
            params,
          })

          this.questions = response.data
          console.log(`[DEBUG QUESTIONS] Domande caricate: ${this.questions.length}`)

          // Salva in entrambe le cache
          this.questionsCache[this.selectedForm] = {
            data: this.questions,
            timestamp: Date.now(),
          }
          try {
            sessionStorage.setItem(questionsKey, JSON.stringify(this.questions))
          } catch (e) {
            console.warn('Impossibile salvare in sessionStorage:', e)
          }

          // Gestione conferenze se necessario
          let needsConferenceOptions = this.questions.some((question) => question.question_type === 'select' && question.question_option?.includes('Elenco_BFC'))

          if (needsConferenceOptions) {
            const conferenceOptions = await this.loadConferenceOptions()
            this.questions = this.questions.map((question) => {
              if (question.question_type === 'select' && question.question_option?.includes('Elenco_BFC')) {
                return {
                  ...question,
                  question_option: [...conferenceOptions, 'Altro'],
                }
              }
              return question
            })

            // Aggiorna le cache con le domande modificate
            this.questionsCache[this.selectedForm] = {
              data: this.questions,
              timestamp: Date.now(),
            }
            try {
              sessionStorage.setItem(questionsKey, JSON.stringify(this.questions))
            } catch (e) {
              console.warn('Impossibile aggiornare cache:', e)
            }
          }

          // Inizializza risposte solo per nuove domande, preservando le esistenti
          this.initializeAnswers()
          return this.questions
        } finally {
          this.isLoadingQuestions = false
        }
      } catch (error) {
        console.error('Errore nel caricamento delle domande:', error)
        throw error
      } finally {
        this.isLoadingQuestions = false
      }
    },

    async loadConferenceOptions() {
      try {
        console.log('[DEBUG] Caricamento conference options da server')
        const response = await api.get('/events', {
          params: { year: 2025, _t: Date.now() }, // Cache busting
        })

        // Return only the title names (no date formatting)
        this.conferenceOptions = response.data.map((conference) => conference.name)

        console.log('[DEBUG] Caricati', this.conferenceOptions.length, 'titoli per Elenco_BFC')
        return this.conferenceOptions
      } catch (error) {
        console.error('Error loading conferences:', error)
        return []
      }
    },

    validateForm() {
      this.errors = {}
      let isValid = true

      this.questions.forEach((question) => {
        if (question.is_mandatory) {
          const answer = this.answers[question.id]
          let isEmpty = false

          // Check based on question type
          if (question.question_type === 'rating') {
            // For rating, null or undefined means not answered
            isEmpty = answer === null || answer === undefined
          } else if (question.question_type === 'checkbox') {
            isEmpty = !Array.isArray(answer) || answer.length === 0
          } else {
            // For text, select, radio
            isEmpty = !answer || (typeof answer === 'string' && answer.trim().length === 0)
          }

          // Check "Altro" option
          const isAltroWithoutCustom =
            (answer === 'Altro' && !this.customAnswers[question.id]) || (Array.isArray(answer) && answer.includes('Altro') && !this.customAnswers[question.id])

          if (isEmpty || isAltroWithoutCustom) {
            this.errors[`question-${question.id}`] = 'Questa domanda è obbligatoria.'
            isValid = false
          }
        }
      })

      return isValid
    },

    async submitAnswers() {
      this.isSubmitted = true
      if (!this.validateForm()) {
        return
      }

      // Process answers, including custom "Altro" responses
      const processedAnswers = { ...this.answers }
      Object.keys(processedAnswers).forEach((key) => {
        if (Array.isArray(processedAnswers[key])) {
          processedAnswers[key] = processedAnswers[key].join('|')
        }
        if (processedAnswers[key] === 'Altro' && this.customAnswers[key]) {
          processedAnswers[key] = `Altro: ${this.customAnswers[key]}`
        }
        if (typeof processedAnswers[key] === 'string') {
          processedAnswers[key] = DOMPurify.sanitize(processedAnswers[key])
        }
      })

      try {
        const payload = {
          answers: processedAnswers,
          formId: this.selectedForm,
          peopleId: this.selectedPerson,
          startTime: this.formStartTime,
          endTime: new Date().toISOString(),
        }
        console.log("Payload inviato all'API:", payload)
        const response = await api.post(`/forms/${this.selectedForm}/answers`, payload)
        if (response.data.success) {
          console.info('Form inviato con successo!')
          this.isFormSubmitted = true
        } else {
          console.error("Errore durante l'invio del form.")
        }
      } catch (error) {
        console.error("Errore durante l'invio:", error)
      }
    },

    updateURL() {
      const url = new URL(globalThis.location)
      url.searchParams.delete('formId')
      url.searchParams.delete('personId')
      globalThis.history.replaceState({}, '', url)
    },

    clearAnswers() {
      this.answers = {}
      this.customAnswers = {}
      this.errors = {}
      this.isSubmitted = false
    },

    initializeAnswers() {
      this.questions.forEach((question) => {
        // Solo inizializza se non esiste già una risposta
        if (this.answers[question.id] === undefined) {
          if (question.question_type === 'rating') {
            this.answers[question.id] = null // null = not answered yet
          } else if (question.question_type === 'checkbox') {
            this.answers[question.id] = []
          } else {
            this.answers[question.id] = ''
          }
        }
      })
    },

    // Set answer for a specific question
    setAnswer(questionId, value) {
      // Force reactivity by creating a new object reference
      this.answers = {
        ...this.answers,
        [questionId]: value,
      }

      // Clear error when answering
      if (this.errors[`question-${questionId}`]) {
        const newErrors = { ...this.errors }
        delete newErrors[`question-${questionId}`]
        this.errors = newErrors
      }

      // Debug log
      console.log(`[ANSWER] Question ${questionId} set to:`, value)
      console.log(`[PROGRESS] Current progress: ${this.formProgress}%`)
    },

    // Set custom answer for "Altro" option
    setCustomAnswer(questionId, value) {
      this.customAnswers[questionId] = value
    },

    // Reset form state
    resetForm() {
      this.clearAnswers()
      this.selectedForm = null
      this.questions = []
      this.formStartTime = null
      this.isFormSubmitted = false
    },

    // Nuovo metodo per preservare risposte durante aggiornamenti
    preserveAnswersAndReload(newQuestions) {
      console.log('[PRESERVE] Salvataggio risposte esistenti...')

      // Salva le risposte attuali
      const existingAnswers = { ...this.answers }
      const existingCustomAnswers = { ...this.customAnswers }

      console.log('[PRESERVE] Risposte salvate:', Object.keys(existingAnswers).length)

      // Aggiorna le domande
      this.questions = newQuestions

      // Ripristina le risposte esistenti che sono ancora valide
      const preservedCount = this.restoreValidAnswers(existingAnswers, existingCustomAnswers)

      console.log(`[PRESERVE] Ripristinate ${preservedCount} risposte su ${Object.keys(existingAnswers).length}`)

      // Inizializza le nuove domande senza risposte
      this.initializeAnswers()
    },

    // Ripristina le risposte che sono ancora valide per le domande attuali
    restoreValidAnswers(savedAnswers, savedCustomAnswers) {
      let preservedCount = 0

      // Ripristina risposte normali
      for (const questionId in savedAnswers) {
        const question = this.questions.find((q) => q.id == questionId)
        if (question) {
          // La domanda esiste ancora, ripristina la risposta
          this.answers[questionId] = savedAnswers[questionId]
          preservedCount++

          console.log(`[PRESERVE] Ripristinata risposta per domanda ${questionId}: ${question.question_type}`)
        } else {
          console.log(`[PRESERVE] Domanda ${questionId} non più presente, risposta eliminata`)
        }
      }

      // Ripristina risposte custom ("Altro")
      for (const questionId in savedCustomAnswers) {
        const question = this.questions.find((q) => q.id == questionId)
        if (question) {
          this.customAnswers[questionId] = savedCustomAnswers[questionId]
          console.log(`[PRESERVE] Ripristinata risposta custom per domanda ${questionId}`)
        }
      }

      return preservedCount
    },

    initFromUrlParams() {
      const urlParams = new URLSearchParams(globalThis.location.search)
      const formId = urlParams.get('formId')
      const personId = urlParams.get('personId')

      if (formId) {
        this.selectedForm = Number.parseInt(formId)
        this.loadForms().then(() => {
          if (personId) {
            this.loadPeople(personId).then(() => {
              if (this.selectedForm && this.selectedPerson) {
                this.loadQuestions()
              }
              this.updateURL()
            })
          } else {
            this.updateURL()
          }
        })
      } else {
        this.loadForms()
      }
    },
    async login(username, password) {
      try {
        const loginData = {
          username: username,
          password: password,
        }
        // 1. Effettua login
        const res = await api.post('/auth/login', loginData)
        // Verifica che la risposta sia consistente con ciò che ci aspettiamo
        if (!res.data.success || !res.data.user) {
          console.error('Risposta login non valida:', res.data)
          throw new Error('Risposta login non valida')
        }
        // 2. Salva i dati utente nello store
        this.user = res.data.user
        this.isAuthenticated = true

        // Normalizza fullName se mancante (safety check)
        if (!this.user.fullName && (this.user.first_name || this.user.last_name)) {
          this.user.fullName = `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim()
          console.log('[LOGIN] fullName calcolato da first_name+last_name:', this.user.fullName)
        }

        // Inizializza anche selectedPerson se disponibile
        if (this.user.people_id) {
          this.selectedPerson = this.user.people_id
          console.log(`Login: impostato selectedPerson=${this.selectedPerson}`)
          if (this.user.company) this.selectedSocieta = this.user.company
          if (this.user.department) this.selectedReparto = this.user.department
        }

        // 3. Salva dati utente nel localStorage per persistenza
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('userData', JSON.stringify(res.data.user))

        console.log(`Login completato con successo: ${res.data.message || 'Utente autenticato'}`)

        // 4. Avvia controllo aggiornamenti
        console.log('[LOGIN] Avvio UPDATE-CHECKER')
        this.startUpdateChecker()

        // 5. Verifica subito la sessione per assicurarsi che tutto funzioni
        try {
          // Verifico che la sessione sia attiva usando /auth/session
          await api.get('/auth/session')
        } catch (sessionError) {
          console.warn('Avviso: Verifica sessione post-login fallita:', sessionError)
          // Non interrompere il flusso di login per questo errore
        }

        return true
      } catch (err) {
        // Gestione errore: pulisci tutti i dati di sessione
        this.user = null
        this.isAuthenticated = false
        this.selectedPerson = null
        this.selectedSocieta = ''
        this.selectedReparto = ''
        localStorage.removeItem('isAuthenticated')
        localStorage.removeItem('userData')
        console.error('Errore durante il login:', err)
        throw err
      }
    },
    async logout() {
      try {
        await api.post('/auth/logout')
      } catch (error) {
        console.error('Errore durante il logout:', error)
      }

      // Ferma il controllo aggiornamenti se attivo
      this.stopUpdateChecker()

      // Pulizia completa di tutti i dati
      this.user = null
      this.isAuthenticated = false
      this.selectedPerson = null
      this.selectedForm = null
      this.questions = []

      // Rimuovi tutti i dati di cache
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('userData')

      // Pulisci cache dati
      this.invalidateAllDataCache()

      console.log('[CACHE] Tutte le cache invalidate al logout')

      // Reindirizza alla pagina di login
      this.navigate('/login')
    },
    async checkSession() {
      // Prima verifica se esiste un flag isAuthenticated nel localStorage
      const isAuthInStorage = localStorage.getItem('isAuthenticated') === 'true'
      const cachedUser = localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')) : null

      // Migration guard: if cached user lacks permissions, clear stale data
      if (cachedUser && cachedUser.permissions === undefined) {
        console.log('[MIGRATION] Stale localStorage detected (missing permissions), clearing')
        localStorage.removeItem('userData')
        localStorage.removeItem('isAuthenticated')
        this.user = null
        this.isAuthenticated = false
      }

      // Solo se esiste un flag di autenticazione o se l'utente è già autenticato, verifica la sessione
      if (isAuthInStorage || this.isAuthenticated) {
        try {
          // Se abbiamo già i dati utente completi in memoria, evitiamo la chiamata API
          if (this.user && Object.keys(this.user).length > 0) {
            console.log('Utilizzando dati utente già in memoria')

            // Normalizza fullName se mancante
            if (!this.user.fullName && (this.user.first_name || this.user.last_name)) {
              this.user.fullName = `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim()
              console.log('[MEMORY] fullName calcolato da first_name+last_name:', this.user.fullName)
            }

            // Assicuriamoci che i dati di selezione siano coerenti con l'utente
            if (this.user.people_id && !this.selectedPerson) {
              this.selectedPerson = this.user.people_id
              console.log(`checkSession: impostato selectedPerson=${this.selectedPerson}`)

              // Imposta anche società e reparto se disponibili
              if (this.user.company) this.selectedSocieta = this.user.company
              if (this.user.department) this.selectedReparto = this.user.department
            }

            return true
          }

          // Se abbiamo dati in localStorage, li utilizziamo temporaneamente per evitare chiamate inutili
          if (cachedUser) {
            console.log('Utilizzando dati utente dal localStorage')

            // Normalizza fullName se mancante
            if (!cachedUser.fullName && (cachedUser.first_name || cachedUser.last_name)) {
              cachedUser.fullName = `${cachedUser.first_name || ''} ${cachedUser.last_name || ''}`.trim()
              console.log('[CACHE] fullName calcolato da first_name+last_name:', cachedUser.fullName)
            }

            this.user = cachedUser
            this.isAuthenticated = true

            // Avvia controllo aggiornamenti
            console.log('[INIT] Avvio UPDATE-CHECKER da localStorage cache')
            this.startUpdateChecker()

            // Imposta anche i dati di persona, società e reparto
            if (cachedUser.people_id) {
              this.selectedPerson = cachedUser.people_id
              if (cachedUser.company) this.selectedSocieta = cachedUser.company
              if (cachedUser.department) this.selectedReparto = cachedUser.department
            }

            // Usiamo il cache in caso di reindirizzamenti rapidi
            if (globalThis.location.pathname === '/login') {
              this.navigate(this.getHomeRoute())
              return true
            }
          }

          // PASSO 1: Verifica la sessione tramite l'endpoint /auth/session
          console.log('Verifica sessione tramite /auth/session')
          const sessionRes = await api.get('/auth/session')

          if (!sessionRes.data?.success) {
            console.warn('Sessione non valida:', sessionRes.data)
            throw new Error('Sessione non valida')
          }

          console.log('Sessione valida:', sessionRes.data.user)

          // PASSO 2: Recupera i dettagli dell'utente tramite /people
          console.log('Recupero dettagli utente completi tramite userId=me')
          const res = await api.get('/people', {
            params: { userId: 'me' }, // Usiamo 'me' per ottenere l'utente corrente
          })

          console.log('Risposta API people:', res.data)

          // Con il nuovo formato, verifichiamo che ci sia success e data
          if (!res.data) {
            console.error('Risposta API /people vuota')
            throw new Error('Dati utente mancanti')
          }

          let userData
          // Gestiamo i due possibili formati di risposta (con o senza .data)
          if (res.data.success && res.data.data) {
            // Nuovo formato: { success: true, data: {...} }
            userData = res.data.data
          } else if (res.data.id) {
            // Vecchio formato: direttamente i dati utente
            userData = res.data
          } else {
            console.error('Formato dati utente non riconosciuto:', res.data)
            throw new Error('Formato dati utente non valido')
          }

          // Normalizza fullName se mancante (potrebbe venire come first_name+last_name)
          if (!userData.fullName && (userData.first_name || userData.last_name)) {
            userData.fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
            console.log('[SESSION] fullName calcolato da first_name+last_name:', userData.fullName)
          }

          // Salva i dati dell'utente
          this.user = userData
          this.isAuthenticated = true
          localStorage.setItem('isAuthenticated', 'true')
          localStorage.setItem('userData', JSON.stringify(userData))

          // Avvia controllo aggiornamenti
          console.log('[SESSION] Avvio UPDATE-CHECKER')
          this.startUpdateChecker()

          // Imposta anche i dati di persona, società e reparto
          if (userData.people_id || userData.id) {
            this.selectedPerson = userData.people_id || userData.id
            console.log(`checkSession: impostato selectedPerson=${this.selectedPerson}`)

            if (userData.company) this.selectedSocieta = userData.company
            if (userData.department) this.selectedReparto = userData.department
          }

          // Redirezione in base al ruolo se siamo nella pagina di login
          if (globalThis.location.pathname === '/login') {
            this.navigate(this.getHomeRoute())
          }

          return true
        } catch (e) {
          console.error('Errore verifica sessione:', e)

          // Pulizia completa
          this.user = null
          this.isAuthenticated = false
          this.selectedPerson = null
          this.selectedSocieta = ''
          this.selectedReparto = ''

          // Pulizia localStorage
          localStorage.removeItem('isAuthenticated')
          localStorage.removeItem('userData')

          // Pulizia sessionStorage (cache domande)
          Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('questions_')) {
              sessionStorage.removeItem(key)
            }
          })

          // Redirezione a login se necessario
          if (globalThis.location.pathname !== '/login') {
            this.navigate('/login')
          }

          return false
        }
      } else if (globalThis.location.pathname !== '/login') {
        // Se non è autenticato e non è nella pagina di login, redirigiamo
        this.navigate('/login')
        return false
      }

      return this.isAuthenticated
    },
    navigate(path) {
      if (this.router) {
        this.router.push(path)
      } else {
        // Fallback if router isn't available in the store
        globalThis.location.href = path
      }
    },

    /**
     * Verifica se l'utente ha accesso alla pagina specificata
     * @param {string} path - Percorso della pagina
     * @returns {boolean} - True se l'utente ha accesso, altrimenti False
     */
    hasAccess(path) {
      // Percorsi pubblici (accessibili senza autenticazione)
      const publicPaths = ['/login', '/access-denied']

      // Se il percorso è pubblico, consenti sempre l'accesso
      if (publicPaths.some((publicPath) => path === publicPath || path.startsWith(publicPath + '/'))) {
        return true
      }

      // Se l'utente non è autenticato, nega l'accesso a tutti gli altri percorsi
      if (!this.isAuthenticated || !this.user) {
        return false
      }

      // Percorsi riservati agli admin
      const adminOnlyPaths = [
        '/admin', // Questo include /admin/home e /admin/password-management
      ]

      // Percorsi riservati agli utenti normali
      const userOnlyPaths = ['/home', '/forms', '/compilazione']

      // Verifica accesso basato sui permessi
      if (adminOnlyPaths.some((adminPath) => path === adminPath || path.startsWith(adminPath + '/'))) {
        return this.hasPermission('admin.home.read')
      }

      if (userOnlyPaths.some((userPath) => path === userPath || path.startsWith(userPath + '/'))) {
        return !this.hasPermission('admin.home.read')
      }

      // Per tutti gli altri percorsi, consenti l'accesso agli utenti autenticati
      return true
    },

    /**
     * Restituisce la home route corretta per l'utente corrente.
     * Usa admin.home.read come discriminante per evitare di escludere admin parziali.
     */
    getHomeRoute() {
      return this.hasPermission('admin.home.read') ? '/admin/home' : '/home'
    },

    /**
     * Reindirizza l'utente alla home appropriata in base al ruolo
     */
    redirectToUserHome() {
      this.navigate(this.getHomeRoute())
    },

    // Controllo aggiornamenti per utenti normali
    startUpdateChecker() {
      // Se c'è già un timer attivo, fermalo prima
      if (this.updateCheckerTimer) {
        console.log('[UPDATE-CHECKER] Fermato timer precedente')
        clearInterval(this.updateCheckerTimer)
      }

      console.log(`[UPDATE-CHECKER] Avviato per utente type_id=${this.user?.type_id} ogni 30 secondi`)

      this.updateCheckerTimer = setInterval(
        async () => {
          try {
            console.log('[UPDATE-CHECKER] Controllo in corso...')

            // Prima controlla se c'è stata un'invalidazione globale
            const isCacheValid = await this.checkCacheValidity()
            let needsUpdate = !isCacheValid

            if (needsUpdate) {
              console.log("[UPDATE-CHECKER] Cache invalidata dall'admin, forzando aggiornamento")
            } else {
              // 1. Controlla aggiornamenti sui forms
              const formsResponse = await api.get('/forms', {
                params: {
                  showHidden: this.showHiddenForms,
                  _t: Date.now(),
                },
              })

              const serverForms = formsResponse.data.forms || formsResponse.data
              const serverFormsCount = serverForms?.length || 0
              const localFormsCount = this.forms.length

              console.log(`[UPDATE-CHECKER] Server: ${serverFormsCount} forms, Locale: ${localFormsCount} forms`)

              // Controlla se il numero di forms è diverso
              if (serverFormsCount !== localFormsCount) {
                console.log(`[UPDATE-CHECKER] Rilevati aggiornamenti forms: server=${serverFormsCount}, locale=${localFormsCount}`)
                needsUpdate = true
              } else if (this.forms.length > 0) {
                // Controlla anche gli ID dei forms per rilevare cambiamenti
                const serverFormIds = serverForms.map((f) => f.id).sort()
                const localFormIds = this.forms.map((f) => f.id).sort()

                if (JSON.stringify(serverFormIds) !== JSON.stringify(localFormIds)) {
                  console.log('[UPDATE-CHECKER] Rilevati cambiamenti negli ID dei forms')
                  needsUpdate = true
                }
              }

              // 2. Controlla aggiornamenti sulle questions (solo se non ci sono già aggiornamenti sui forms)
              if (!needsUpdate) {
                // Prima, controlla il form attualmente selezionato (priorità alta)
                if (this.selectedForm) {
                  try {
                    const questionsParams = { _t: Date.now() }
                    const roleValue = this.user?.training_path || this.user?.percorso_formativo
                    if (roleValue) {
                      questionsParams.role = roleValue
                    }
                    const questionsResponse = await api.get(`/forms/${this.selectedForm}/questions`, {
                      params: questionsParams,
                    })

                    const serverQuestions = questionsResponse.data
                    const serverQuestionsCount = serverQuestions.length
                    const localQuestionsCount = this.questions.length

                    if (serverQuestionsCount !== localQuestionsCount) {
                      console.log(`[UPDATE-CHECKER] Aggiornamenti questions rilevati per form ${this.selectedForm}`)
                      needsUpdate = true
                    } else if (this.questions.length > 0) {
                      // Controlla anche gli ID delle domande
                      const serverQuestionIds = serverQuestions.map((q) => q.id).sort()
                      const localQuestionIds = this.questions.map((q) => q.id).sort()

                      if (JSON.stringify(serverQuestionIds) !== JSON.stringify(localQuestionIds)) {
                        console.log('[UPDATE-CHECKER] Cambiamenti ID questions rilevati')
                        needsUpdate = true
                      }
                    }
                  } catch (questionsError) {
                    console.warn('[UPDATE-CHECKER] Errore controllo questions form selezionato:', questionsError)
                  }
                }

                // Poi, controlla le cache per gli altri forms (controllo limitato)
                if (!needsUpdate) {
                  // Controlla solo i forms che hanno già cache questions
                  for (const formId in this.questionsCache) {
                    if (formId == this.selectedForm) continue // Già controllato sopra

                    const cachedQuestions = this.questionsCache[formId]
                    if (!cachedQuestions?.data) continue

                    try {
                      const questionsParams = { _t: Date.now() }
                      const roleValue = this.user?.training_path || this.user?.percorso_formativo
                      if (roleValue) {
                        questionsParams.role = roleValue
                      }
                      const questionsResponse = await api.get(`/forms/${formId}/questions`, {
                        params: questionsParams,
                      })

                      const serverQuestions = questionsResponse.data
                      const serverQuestionsCount = serverQuestions.length
                      const localQuestionsCount = cachedQuestions.data.length

                      if (serverQuestionsCount !== localQuestionsCount) {
                        console.log(`[UPDATE-CHECKER] Form ${formId}: aggiornamenti questions rilevati`)
                        needsUpdate = true
                        break
                      }

                      // Controlla anche gli ID delle domande
                      const serverQuestionIds = serverQuestions.map((q) => q.id).sort()
                      const localQuestionIds = cachedQuestions.data.map((q) => q.id).sort()

                      if (JSON.stringify(serverQuestionIds) !== JSON.stringify(localQuestionIds)) {
                        console.log(`[UPDATE-CHECKER] Form ${formId}: cambiamenti ID questions`)
                        needsUpdate = true
                        break
                      }
                    } catch (questionsError) {
                      console.warn(`[UPDATE-CHECKER] Errore controllo questions form ${formId}:`, questionsError)
                    }
                  }
                }
              }
            }

            // 3. Se servono aggiornamenti, ricarica i dati
            if (needsUpdate) {
              console.log('[UPDATE-CHECKER] Aggiornamento necessario')

              // Invalida tutta la cache
              this.invalidateAllDataCache()

              // Ricarica automaticamente se siamo nella pagina giusta
              if (globalThis.location.pathname.includes('/forms') || globalThis.location.pathname.includes('/home') || globalThis.location.pathname.includes('/compilazione')) {
                console.log('[UPDATE-CHECKER] Ricarico dati automaticamente')

                // Ricarica forms
                await this.loadForms(true)

                // Se c'è un form selezionato, ricarica anche le domande PRESERVANDO le risposte
                if (this.selectedForm) {
                  console.log(`[UPDATE-CHECKER] Ricarico domande per form ${this.selectedForm} preservando risposte`)

                  // Carica le nuove domande dal server
                  try {
                    const questionsParams = { _t: Date.now() }
                    const roleValue = this.user?.training_path || this.user?.percorso_formativo
                    if (roleValue) {
                      questionsParams.role = roleValue
                    }
                    const questionsResponse = await api.get(`/forms/${this.selectedForm}/questions`, {
                      params: questionsParams,
                    })

                    const newQuestions = questionsResponse.data

                    // Verifica se l'utente ha risposte da preservare
                    const hasAnswers = Object.keys(this.answers).some((key) => {
                      const answer = this.answers[key]
                      if (typeof answer === 'number') return answer > 0
                      if (Array.isArray(answer)) return answer.length > 0
                      return Boolean(answer?.trim?.())
                    })

                    if (hasAnswers) {
                      console.log('[UPDATE-CHECKER] Preservo risposte utente')
                      // Usa il metodo di preservazione delle risposte
                      this.preserveAnswersAndReload(newQuestions)
                    } else {
                      // Ricarica normalmente
                      await this.loadQuestions(true)
                    }

                    // Aggiorna anche la cache
                    this.questionsCache[this.selectedForm] = {
                      data: this.questions,
                      timestamp: Date.now(),
                    }

                    try {
                      const questionsKey = `questions_${this.selectedForm}`
                      sessionStorage.setItem(questionsKey, JSON.stringify(this.questions))
                    } catch (e) {
                      console.warn('Impossibile salvare in sessionStorage:', e)
                    }
                  } catch (error) {
                    console.warn('[UPDATE-CHECKER] Errore ricaricamento questions:', error)
                    // Fallback a ricaricamento normale
                    await this.loadQuestions(true)
                  }
                }
              }

              // Mostra notifica discreta all'utente (sempre, indipendentemente dalla pagina)
              this.showUpdateNotification('Dati aggiornati dall\'amministratore')
            } else {
              console.log('[UPDATE-CHECKER] Nessun cambiamento rilevato')
            }
          } catch (error) {
            console.warn('[UPDATE-CHECKER] Errore controllo aggiornamenti:', error)
          }
        },
        30 * 1000 // 30 secondi per test
      )
    },

    // Ferma il controllo aggiornamenti
    stopUpdateChecker() {
      if (this.updateCheckerTimer) {
        console.log('[UPDATE-CHECKER] Fermato')
        clearInterval(this.updateCheckerTimer)
        this.updateCheckerTimer = null
      }
    },

    // Notifica discreta all'utente
    showUpdateNotification(message = 'Questionario aggiornato - risposte preservate', type = 'success') {
      const bgColor = type === 'error' ? '#EF4444' : '#10B981'
      // Crea una notifica temporanea
      const notification = document.createElement('div')
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${bgColor};
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 9999;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 14px;
        animation: slideIn 0.3s ease;
      `
      notification.textContent = message

      // Aggiungi CSS per l'animazione
      if (!document.querySelector('#update-animation-style')) {
        const style = document.createElement('style')
        style.id = 'update-animation-style'
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `
        document.head.appendChild(style)
      }

      document.body.appendChild(notification)

      // Rimuovi dopo 3 secondi
      setTimeout(() => {
        notification.style.animation = 'slideIn 0.3s ease reverse'
        setTimeout(() => {
          if (notification.parentNode) {
            notification.remove()
          }
        }, 300)
      }, 3000)
    },

    // Metodo per ricaricare questions preservando le risposte esistenti
    async refreshQuestionsPreservingAnswers() {
      if (!this.selectedForm) return

      console.log('[PRESERVE] Ricaricamento questions con preservazione risposte')

      try {
        // Carica le nuove domande dal server
        const questionsParams = { _t: Date.now() }
        const roleValue = this.user?.training_path || this.user?.percorso_formativo
        if (roleValue) {
          questionsParams.role = roleValue
        }
        const response = await api.get(`/forms/${this.selectedForm}/questions`, {
          params: questionsParams,
        })

        const newQuestions = response.data

        // Verifica se l'utente ha risposte da preservare
        const hasAnswers = Object.keys(this.answers).some((key) => {
          const answer = this.answers[key]
          if (typeof answer === 'number') return answer > 0
          if (Array.isArray(answer)) return answer.length > 0
          return Boolean(answer?.trim?.())
        })

        if (hasAnswers) {
          console.log('[PRESERVE] Preservo risposte esistenti durante refresh admin')
          this.preserveAnswersAndReload(newQuestions)
        } else {
          console.log('[PRESERVE] Nessuna risposta da preservare, reload normale')
          await this.loadQuestions(true)
        }

        return this.questions
      } catch (error) {
        console.error('[PRESERVE] Errore durante refresh questions:', error)
        // Fallback a reload normale
        return await this.loadQuestions(true)
      }
    },

    // Metodo per forzare invalidazione cache globale (solo admin)
    async forceGlobalCacheInvalidation() {
      try {
        console.log('[ADMIN] Richiedendo invalidazione cache globale...')

        const response = await api.post('/admin/force-cache-invalidation', {
          adminSecret: import.meta.env.VITE_ADMIN_SECRET || 'admin_secret_fallback',
        })

        if (response.data.success) {
          console.log(`[ADMIN] Cache invalidata globalmente. Timestamp: ${response.data.timestamp}`)

          // Salva il timestamp globale di invalidazione
          localStorage.setItem('globalCacheInvalidationTimestamp', response.data.timestamp.toString())

          return response.data
        } else {
          throw new Error(response.data.error || 'Invalidazione fallita')
        }
      } catch (error) {
        console.error('[ADMIN] Errore durante invalidazione cache globale:', error)
        throw error
      }
    },

    // Metodo per verificare se la cache locale è ancora valida
    async checkCacheValidity() {
      try {
        const response = await api.get('/admin/cache-status')

        if (response.data.success) {
          const serverTimestamp = response.data.invalidationTimestamp
          const localTimestamp = Number.parseInt(localStorage.getItem('globalCacheInvalidationTimestamp') || '0')

          console.log('[CACHE-CHECK] Server timestamp:', serverTimestamp, 'Local timestamp:', localTimestamp)

          // Se il timestamp del server è più recente, la cache locale non è valida
          if (serverTimestamp > localTimestamp) {
            console.log('[CACHE-CHECK] Cache invalidata, aggiornamento richiesto')
            // Aggiorna il timestamp locale per evitare ricaricamenti multipli
            localStorage.setItem('globalCacheInvalidationTimestamp', serverTimestamp.toString())
            return false
          }

          console.log('[CACHE-CHECK] Cache valida')
          return true
        }

        return true // Se non riusciamo a verificare, assumiamo che sia valida
      } catch (error) {
        // Se è un errore 401, significa che la sessione è scaduta
        if (error.response?.status === 401) {
          console.warn('[CACHE-CHECK] Sessione scaduta (401) - fermo UPDATE-CHECKER')
          this.stopUpdateChecker()
          return true // Non invalidiamo la cache, l'utente verrà reindirizzato al login
        }
        console.warn('[CACHE] Errore verifica validità cache:', error)
        return true // In caso di errore, non invalidiamo la cache
      }
    },
  },
})
